// AI-CONTEXT: See docs/methodiek-benchmark-model.md § 1 en § 2
import getBenchmarkStrength, { getBenchmarkNextLevelGaps } from '../dataHelpers/getBenchmarkStrength'

/**
 * Rijen voor de MSI-benchmarkkaart, uit drie soorten benchmark.
 *
 * De kaart toont ze onder elkaar, maar ze meten niet hetzelfde. Dat verschil moet
 * in de rij zitten en niet in het hoofd van de lezer:
 *
 * - `effect`  — groei op een MSI-effect, in procent van de schaal.
 * - `groei`   — groei op een MSI-output, in de eenheid van die output (treden,
 *               Likert-punten). Nooit opgeteld bij een andere eenheid.
 * - `niveau`  — een niveau per eenheid, geen groei. Een huishouden krijgt
 *               maatregelen of niet; er is geen voor- en nameting. Een niveau in
 *               dezelfde kolom als een groei is alleen leesbaar als de rij zegt
 *               dat het een niveau is.
 */
// Bij welke sector een OUTPUT hoort. Dit staat bewust hier en niet in de
// sectorcatalogus: die volgt `effect.sectors[]` uit de meetstandaard-api, en
// outputs komen daar niet in voor. Het is dus een productfeit, geen afgeleide —
// vandaar expliciet, met de bron erbij (gebruikersbevestiging 2026-08-31).
export const OUTPUT_SECTOR = {
    arbeidsvaardigheden: 'arbeidsparticipatie',
    participatieladder: 'arbeidsparticipatie',
    materialen: 'energiearmoede',
}

// De twee niveau-benchmarks per huishouden. Stond als lokale constante in
// de MSI-benchmarkkaart op het dashboard (inmiddels vervangen door het
// tabblad Benchmarks); gedeeld zodra een tweede scherm ze nodig had.
// AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
export const MATERIAL_OUTPUTS = [
    // `shortUnit` staat achter elke waarde in de stippengrafiek.
    { output: 'co2Kg', effectName: 'Bespaarde CO2', unit: 'kg per huishouden', shortUnit: 'kg' },
    { output: 'privateEur', effectName: 'Bespaard op energierekening', unit: '€ per huishouden', shortUnit: '€' },
]

export const EFFECT = 'effect'
export const GROEI_OUTPUT = 'groei'
export const NIVEAU_OUTPUT = 'niveau'

const sterkte = (benchmark, perCompanyKey = 'respondentsPerCompany') => {
    const invoer = {
        companyCount: benchmark.companyCount,
        respondentCount: benchmark.respondentCount,
        respondentsPerCompany: benchmark[perCompanyKey],
    }
    const s = getBenchmarkStrength(invoer)
    return {
        largestShare: s.largestShare,
        strengthLabel: s.label,
        strengthScore: s.finalScore,
        nextLevelGaps: getBenchmarkNextLevelGaps(invoer),
    }
}

/**
 * Rij voor een MSI-effect (msiBenchmark).
 *
 * @param {{category: string, effectId: string, effectName: string, benchmark: object}} input
 */
export const effectRow = ({ category, effectId, effectName, sectors = [], benchmark = {} }) => ({
    kind: EFFECT,
    category,
    // Uit de meetstandaard-catalogus (`effect.sectors[]`); een effect kan in
    // meer dan één sector voorkomen.
    sectors: Array.isArray(sectors) ? sectors : [],
    rowId: effectId,
    effectId,
    effectName,
    value: benchmark.benchmarkGrowth ?? null,
    unit: '%',
    companyCount: benchmark.companyCount || 0,
    respondentCount: benchmark.respondentCount || 0,
    detailPath: (client) => `/${client}/benchmarkdetails/${effectId}`,
    ...sterkte(benchmark),
})

/**
 * Rij voor de participatieladder (participationLadderBenchmark).
 *
 * @param {object} benchmark
 */
export const ladderRow = (benchmark = {}) => ({
    kind: GROEI_OUTPUT,
    category: 'Output',
    rowId: 'participatieladder',
    effectName: 'Participatieladder',
    sectors: [OUTPUT_SECTOR.participatieladder],
    value: benchmark.benchmarkGrowth ?? null,
    unit: 'treden',
    companyCount: benchmark.companyCount || 0,
    respondentCount: benchmark.respondentCount || 0,
    detailPath: (client) => `/${client}/participatiebenchmark`,
    ...sterkte(benchmark),
})

/**
 * Rij voor één effect van MSI Arbeidsvaardigheden (workSkillsBenchmark).
 *
 * Arbeidsvaardigheden is een output en geen effect: de begeleider legt een
 * oordeel vast, de deelnemer rapporteert niet over zichzelf. Er wordt per effect
 * gebenchmarkt en niet over de negen heen — dat gemiddelde kent de standaard niet.
 *
 * @param {{effectId: string, effectName: string, benchmark: object}} input
 */
export const workSkillsRow = ({ effectId, effectName, benchmark = {} }) => ({
    kind: GROEI_OUTPUT,
    category: 'Output · arbeidsvaardigheden',
    rowId: `arbeidsvaardigheden-${effectId}`,
    effectName,
    sectors: [OUTPUT_SECTOR.arbeidsvaardigheden],
    value: benchmark.benchmarkGrowth ?? null,
    unit: 'punten',
    companyCount: benchmark.companyCount || 0,
    respondentCount: benchmark.respondentCount || 0,
    detailPath: null,
    ...sterkte(benchmark),
})

/**
 * Rij voor een materialen-output (materialOutputBenchmark).
 *
 * Geen sterktescore: benchmarkmodel § 8 laat open welke teleenheid telt bij een
 * niveau-benchmark. Die score verzinnen zou de rij sterker laten lijken dan hij
 * is, en § 6.1 hangt de publicatiedrempel er juist aan op.
 *
 * @param {{output: string, effectName: string, unit: string, benchmark: object}} input
 */
export const materialRow = ({ output, effectName, unit, benchmark = {} }) => ({
    kind: NIVEAU_OUTPUT,
    // Welke output: de sleutel van de detailpagina en van materialOutputBenchmark.
    output,
    category: 'Output · materialen',
    rowId: `materialen-${output}`,
    effectName,
    sectors: [OUTPUT_SECTOR.materialen],
    value: benchmark.benchmarkLevel ?? null,
    unit,
    unitTitle: benchmark.unit || null,
    companyCount: benchmark.companyCount || 0,
    respondentCount: benchmark.householdCount || 0,
    largestShare: null,
    strengthLabel: null,
    strengthScore: null,
    nextLevelGaps: null,
    detailPath: null,
})

/**
 * De waarde van een rij, met zijn eenheid. Zonder eenheid is het getal niet te
 * lezen: 0,4 is geen 0,4% en 42 kg is geen groei.
 *
 * @param {object} row
 * @returns {string}
 */
export const formatBenchmarkValue = (row) => {
    if (row.value == null) return '—'
    if (row.kind === EFFECT) return `${row.value}%`
    return `${row.value} ${row.unit}`
}

/**
 * Sorteervolgorde van de kaart: eerst de outputs (vaste volgorde, zodat de kaart
 * niet danst bij elke herberekening), daarna de effecten op sterkte.
 *
 * @param {{outputRows: Array<object>, effectRows: Array<object>}} input
 * @returns {Array<object>}
 */
export const orderRows = ({ outputRows = [], effectRows = [] }) => [
    ...outputRows,
    ...[...effectRows].sort((a, b) => (b.strengthScore || 0) - (a.strengthScore || 0)),
]
